import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { CorporateEnrollmentService, MasterPaymentPlan } from '../../../services/corporate-enrollment.service';
import { PaymentPlansService } from '../../../services/payment-plans.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { PaymentPlanRefinanceService } from '../../../services/payment-plan-refinance.service';
import {
  EditablePaymentPlanVersionReasons,
  PaymentPlanVersionReason,
  PaymentPlanVersionReasonLabels
} from '../../../enums/PaymentPlanVersionReason';

@Component({
  selector: 'app-edit-corporate-participant-plans',
  templateUrl: './edit-corporate-participant-plans.component.html',
  styleUrls: ['./edit-corporate-participant-plans.component.scss']
})
export class EditCorporateParticipantPlansComponent implements OnInit {
  form!: FormGroup;
  agreementId!: number;
  participantId!: number;

  // 🧮 Validaciones y cálculos
  totalAssignedAmount = 0;
  remainingBalance = 0;
  amountPaid = 0;
  isExactMatch = false;
  exceedsTotalAmount = false;
  totalAmount = 0;

  // 🆕 Baseline inmutable para recompute
  private baselineEditable: any[] = [];
  private baselineTotalAmount = 0;
  private baselineAmountPaid = 0;
  addedInstallments: any[] = [];
  private unplacedRedistributes = new Set<number>();

  // 🆕 Modal auto-crear cuota
  showCreateInstallmentModal = false;
  pendingPartialPlan: any = null;
  pendingShortfall = 0;
  newInstallmentDate = '';

  // 🆕 Modal de refinanciación
  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 12;

  // 🏢 Información corporativa
  corporateInfo: any = null;
  participantInfo: any = null;

  // 🔧 Exponer Math para uso en template
  Math = Math;

  // 🆕 Motivo + comentario obligatorio al guardar (versionado por grupo)
  showReasonModal = false;
  reasonChoices = EditablePaymentPlanVersionReasons.map(r => ({
    value: r,
    label: PaymentPlanVersionReasonLabels[r]
  }));
  selectedReason: PaymentPlanVersionReason = PaymentPlanVersionReason.Refinance;
  reasonComment = '';
  PaymentPlanVersionReason = PaymentPlanVersionReason;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private corporateService: CorporateEnrollmentService,
    private paymentPlansService: PaymentPlansService,
    private toast: ToastService,
    private loading: LoadingService,
    private router: Router,
    private refinanceSvc: PaymentPlanRefinanceService
  ) {}

  excessSummary: { installmentNumber: number; receiptId: number; excessPaymentId: number; amount: number }[] = [];
  // 🆕 Snapshot de cuotas PAID + PARTIAL
  paidInstallments: any[] = [];
  partialInstallments: any[] = [];
  showPaidSection = false;
  showPartialSection = true;
  redistributionLogLines: string[] = [];
  partialActions: Record<number, 'keep' | 'close-redistribute' | 'close-loss'> = {};

  /** 🆕 Cuotas ordenadas asc preservando índice real del FormArray. */
  get sortedPlansWithIndex(): Array<{ ctrl: any; idx: number }> {
    return this.plans.controls
      .map((ctrl, idx) => ({ ctrl, idx }))
      .sort((a, b) => Number(a.ctrl.get('installmentNumber')?.value || 0)
                    - Number(b.ctrl.get('installmentNumber')?.value || 0));
  }

  // trackBy por INSTANCIA del control (no por índice): al reconstruir el FormArray
  // (rebuildFromBaseline) los controls son nuevos → Angular recrea las filas y el
  // formGroupName se re-resuelve. Trackear por índice reusaba el DOM viejo y dejaba
  // el input desincronizado del control. Al tipear el ctrl es estable → foco intacto.
  trackByIdx = (_: number, item: { ctrl: any; idx: number }) => item.ctrl;

  /**
   * 🆕 Recalcula las cuotas EDITABLES posteriores a `currentIdx` con el saldo restante.
   */
  recalculateFromInstallment(currentIdx: number): void {
    const currentCtrl = this.plans.at(currentIdx);
    const currentNumber = Number(currentCtrl.get('installmentNumber')?.value || 0);

    let frozen = this.paidInstallments.reduce(
      (s, p) => s + (Number(p.amountToPay) || 0), 0);

    this.plans.controls.forEach(c => {
      const num = Number(c.get('installmentNumber')?.value || 0);
      const status = c.get('paymentStatus')?.value;
      const amount = this.parseFormattedCurrency(c.get('amountToPay')?.value || 0);
      if (status === 'PAID' || status === 'PARTIALLY_PAID' || num <= currentNumber) {
        frozen += amount;
      }
    });

    const candidates = this.plans.controls.filter(c => {
      const num = Number(c.get('installmentNumber')?.value || 0);
      const status = c.get('paymentStatus')?.value;
      return num > currentNumber
          && status !== 'PAID'
          && status !== 'PARTIALLY_PAID';
    });

    if (candidates.length === 0) {
      this.toast.showToast('warning', 'No hay cuotas editables posteriores para recalcular.');
      return;
    }

    const remaining = this.roundToTwo(this.totalAmount - frozen);
    if (remaining <= 0) {
      this.toast.showToast('warning', 'Las cuotas anteriores ya cubren el total.');
      return;
    }

    const amounts = this.refinanceSvc.splitEvenly(remaining, candidates.length);
    candidates.forEach((c, i) => {
      c.get('amountToPay')?.setValue(amounts[i]);
    });
    this.validateAssignedAmount();
    const formatted = remaining.toLocaleString('es-CO',
      { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    this.toast.showToast('success',
      `Saldo de ${formatted} redistribuido en ${candidates.length} cuota${candidates.length === 1 ? '' : 's'} posterior${candidates.length === 1 ? '' : 'es'}.`);
  }

  ngOnInit(): void {
    this.agreementId = Number(this.route.snapshot.paramMap.get('agreementId'));
    this.participantId = Number(this.route.snapshot.paramMap.get('participantId'));
    this.form = this.fb.group({
      totalAmount: ['', Validators.required],
      plans: this.fb.array([])
    });
    this.load();
    
    // 🔧 Exponer componente para debug global (solo desarrollo)
    (window as any).debugCorporateComponent = this;
    console.log('🔧 Componente corporativo expuesto globalmente. Usar: debugCorporateComponent.debugValidation()');
  }

  get plans(): FormArray { return this.form.get('plans') as FormArray; }

  private createPlanGroup(): FormGroup {
    return this.fb.group({
      dueDate: ['', Validators.required],
      installmentNumber: [1, Validators.required],
      amountToPay: ['', Validators.required],
      amountPaid: [0],
      paymentStatus: ['PENDING', Validators.required],
      payerType: ['PARTICIPANT', Validators.required],
      isPunished: [false],
      observations: ['']
    });
  }

  private load(): void {
    this.loading.show();

    // 🆕 Usamos el snapshot completo del participante (PAID + PARTIAL + EDITABLE)
    Promise.all([
      this.paymentPlansService.getEditableSnapshot(this.agreementId, 'corporate-participant', this.participantId).toPromise(),
      this.corporateService.getAgreementDetails(this.agreementId).toPromise()
    ])
    .then(([snapshotResponse, detailsResponse]) => {
      const snapshot = (snapshotResponse as any)?.data ?? snapshotResponse;
      const allInstallments: any[] = snapshot?.installments ?? [];

      const corporateDetails = (detailsResponse as any)?.data || detailsResponse;
      this.setupCorporateInfo(corporateDetails);

      this.paidInstallments = allInstallments.filter(p => p.editability === 'READ_ONLY');
      this.partialInstallments = allInstallments.filter(p => p.editability === 'CLOSE_OR_KEEP');
      const editable = allInstallments.filter(p => p.editability === 'EDITABLE');

      this.baselineEditable = editable.map(p => ({ ...p }));
      const sumCuotas = allInstallments.reduce((s, p) => s + (p.amountToPay || 0), 0);
      const matricula = Number(snapshot?.matriculaTotal ?? 0);
      this.baselineTotalAmount = matricula > 0 ? matricula : sumCuotas; // ancla a matrícula
      this.baselineAmountPaid = allInstallments.reduce((s, p) => s + (p.amountPaid || 0), 0);
      this.addedInstallments = [];

      this.partialActions = {};
      this.partialInstallments.forEach(p => this.partialActions[p.id] = 'keep');

      this.rebuildFromBaseline();
      setTimeout(() => this.validateAssignedAmount(), 100);
    })
    .catch((error) => {
      console.error('Error cargando datos corporativos:', error);
      this.toast.showToast('error', 'No se pudieron cargar los planes');
    })
    .finally(() => this.loading.hide());
  }

  private rebuildFromBaseline(): void {
    this.totalAmount = this.baselineTotalAmount;
    this.amountPaid = this.baselineAmountPaid;
    this.remainingBalance = this.roundToTwo(this.baselineTotalAmount - this.baselineAmountPaid);
    this.form.patchValue({ totalAmount: this.totalAmount }, { emitEvent: false });
    this.redistributionLogLines = [];

    this.plans.clear();
    [...this.baselineEditable, ...this.addedInstallments].forEach((p: any) => {
      const g = this.createPlanGroup();
      g.patchValue({
        dueDate: new Date(p.dueDate),
        installmentNumber: p.installmentNumber,
        amountToPay: p.amountToPay,
        amountPaid: p.amountPaid || 0,
        paymentStatus: p.paymentStatus || 'PENDING',
        payerType: 'PARTICIPANT',
        isPunished: p.isPunished || false,
        observations: p.observations || ''
      });
      (g as any).installmentId = p.id ?? null;
      g.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.plans.push(g);
    });

    this.unplacedRedistributes.clear();
    this.partialInstallments.forEach(p => {
      const action = this.partialActions[p.id] || 'keep';
      const placed = this.applyPartialActionCore(p, action);
      if (!placed) this.unplacedRedistributes.add(p.id);
    });

    this.validateAssignedAmount();
  }

  private applyPartialActionCore(plan: any, action: 'keep' | 'close-redistribute' | 'close-loss'): boolean {
    const paid = plan.amountPaid || 0;
    const original = plan.amountToPay || 0;
    const shortfall = Math.max(0, this.roundToTwo(original - paid));

    const g = this.createPlanGroup();
    if (action === 'keep') {
      g.patchValue({
        dueDate: new Date(plan.dueDate),
        installmentNumber: plan.installmentNumber,
        amountToPay: original,
        amountPaid: paid,
        paymentStatus: 'PARTIALLY_PAID',
        payerType: 'PARTICIPANT',
        isPunished: plan.isPunished || false,
        observations: plan.observations || ''
      });
    } else {
      const obsSuffix = action === 'close-redistribute'
        ? `Cerrada en $${paid.toLocaleString('es-CO')} (era $${original.toLocaleString('es-CO')}). Saldo $${shortfall.toLocaleString('es-CO')} redistribuido.`
        : `Cerrada en $${paid.toLocaleString('es-CO')} (era $${original.toLocaleString('es-CO')}). Saldo $${shortfall.toLocaleString('es-CO')} descontado del total.`;
      g.patchValue({
        dueDate: new Date(plan.dueDate),
        installmentNumber: plan.installmentNumber,
        amountToPay: paid,
        amountPaid: paid,
        paymentStatus: 'PAID',
        payerType: 'PARTICIPANT',
        isPunished: plan.isPunished || false,
        observations: this.appendObservation(plan.observations, obsSuffix)
      });
    }
    (g as any).installmentId = plan.id;
    g.valueChanges.subscribe(() => this.validateAssignedAmount());
    this.plans.push(g);

    if (action === 'close-redistribute' && shortfall > 0) {
      return this.redistributeShortfall(plan, shortfall);
    }
    if (action === 'close-loss' && shortfall > 0) {
      this.remainingBalance = this.roundToTwo(this.remainingBalance - shortfall);
      this.totalAmount = this.roundToTwo(this.totalAmount - shortfall);
      this.form.patchValue({ totalAmount: this.totalAmount }, { emitEvent: false });
      this.redistributionLogLines.push(
        `Cuota #${plan.installmentNumber}: cerrada en $${paid.toLocaleString('es-CO')}; saldo $${shortfall.toLocaleString('es-CO')} marcado como pérdida.`
      );
    }
    return true;
  }

  // 🆕 Acción sobre cuotas PARCIALES
  applyPartialAction(plan: any, action: 'keep' | 'close-redistribute' | 'close-loss', showToast = true): void {
    this.partialActions[plan.id] = action;
    this.rebuildFromBaseline();

    const shortfall = Math.max(0, this.roundToTwo((plan.amountToPay || 0) - (plan.amountPaid || 0)));

    if (action === 'close-redistribute' && shortfall > 0 && this.unplacedRedistributes.has(plan.id)) {
      this.openCreateInstallmentModal(plan);
      return;
    }

    if (showToast && action === 'close-redistribute' && shortfall > 0) {
      this.toast.showToast('success', `Saldo de $${shortfall.toLocaleString('es-CO')} redistribuido.`);
    }
    if (showToast && action === 'close-loss' && shortfall > 0) {
      this.toast.showToast('warning', `Saldo de $${shortfall.toLocaleString('es-CO')} descontado del total.`);
    }
  }

  private redistributeShortfall(partialPlan: any, shortfall: number): boolean {
    const candidates = this.plans.controls.filter(c => {
      const status = c.get('paymentStatus')?.value;
      const num = c.get('installmentNumber')?.value;
      return (status === 'PENDING' || status === 'OVERDUE' || status === 'LINK_GENERATED')
        && num > partialPlan.installmentNumber;
    });
    if (candidates.length === 0) return false;

    const deltas = this.refinanceSvc.splitEvenly(shortfall, candidates.length);
    const affected: string[] = [];
    candidates.forEach((ctrl, i) => {
      const delta = deltas[i];
      const cur = this.parseFormattedCurrency(ctrl.get('amountToPay')?.value || '0');
      ctrl.get('amountToPay')?.setValue(this.roundToTwo(cur + delta), { emitEvent: false });
      affected.push(`#${ctrl.get('installmentNumber')?.value} (+$${delta.toLocaleString('es-CO')})`);
    });
    this.redistributionLogLines.push(
      `Cuota #${partialPlan.installmentNumber}: cerrada en $${(partialPlan.amountPaid || 0).toLocaleString('es-CO')}. ` +
      `Saldo $${shortfall.toLocaleString('es-CO')} redistribuido en ${affected.join(', ')}.`
    );
    return true;
  }

  private openCreateInstallmentModal(plan: any): void {
    this.pendingPartialPlan = plan;
    this.pendingShortfall = Math.max(0, this.roundToTwo((plan.amountToPay || 0) - (plan.amountPaid || 0)));
    this.newInstallmentDate = this.defaultNextDueDateInput();
    this.showCreateInstallmentModal = true;
  }

  private defaultNextDueDateInput(): string {
    const times = [
      ...this.plans.controls.map(c => new Date(c.get('dueDate')?.value).getTime()),
      ...this.paidInstallments.map(p => new Date(p.dueDate).getTime())
    ].filter(t => !isNaN(t));
    const base = times.length ? new Date(Math.max(...times)) : new Date();
    base.setMonth(base.getMonth() + 1);
    const y = base.getFullYear();
    const m = ('0' + (base.getMonth() + 1)).slice(-2);
    const d = ('0' + base.getDate()).slice(-2);
    return `${y}-${m}-${d}`;
  }

  confirmCreateInstallment(): void {
    if (!this.pendingPartialPlan) return;
    // num se calcula ANTES de pushear; firstFreeInstallmentNumber lee el FormArray
    // ya reconstruido del rebuild previo, así dos cuotas auto-creadas no colisionan.
    const num = this.firstFreeInstallmentNumber;
    this.addedInstallments.push({
      id: null,
      dueDate: this.newInstallmentDate,
      installmentNumber: num,
      amountToPay: 0, // arranca en 0: redistributeShortfall (en el rebuild) la llena con el saldo. Sembrarla con el shortfall lo contaría doble.
      amountPaid: 0,
      paymentStatus: 'PENDING',
      payerType: 'PARTICIPANT',
      isPunished: false,
      observations: `Cuota creada para absorber saldo de cuota #${this.pendingPartialPlan.installmentNumber}`
    });
    const created = num;
    const amount = this.pendingShortfall;
    this.pendingPartialPlan = null;
    this.showCreateInstallmentModal = false;
    this.rebuildFromBaseline();
    this.toast.showToast('success',
      `Cuota #${created} creada por $${amount.toLocaleString('es-CO')} para absorber el saldo.`);
  }

  cancelCreateInstallment(): void {
    if (this.pendingPartialPlan) this.partialActions[this.pendingPartialPlan.id] = 'keep';
    this.pendingPartialPlan = null;
    this.showCreateInstallmentModal = false;
    this.rebuildFromBaseline();
  }

  private appendObservation(existing: string | null | undefined, suffix: string): string {
    if (!existing) return suffix;
    return existing.includes(suffix) ? existing : `${existing} | ${suffix}`;
  }

  togglePaidSection(): void { this.showPaidSection = !this.showPaidSection; }
  togglePartialSection(): void { this.showPartialSection = !this.showPartialSection; }


  private setupCorporateInfo(details: any): void {
    console.log('🏢 Configurando información corporativa:', details);
    
    if (details) {
      // Información de la empresa
      this.corporateInfo = {
        agreementId: this.agreementId,
        companyName: details.company?.name || details.companyName || 'Empresa Corporativa',
        companyNIT: details.company?.documentId || details.companyNIT || details.nit || 'N/A',
        projectName: details.project?.name || details.projectName || 'N/A'
      };
      
      console.log('🏢 Información corporativa configurada:', this.corporateInfo);
      
      // Buscar información del participante específico
      let participant = null;
      
      if (details.participantEnrollments && Array.isArray(details.participantEnrollments)) {
        // Buscar el participante específico por ID en las inscripciones corporativas
        const participantEnrollment = details.participantEnrollments.find((pe: any) => 
          pe.enrollment?.participant?.id === this.participantId
        );
        participant = participantEnrollment?.enrollment?.participant;
        
        console.log('🔍 Buscando participante con ID:', this.participantId);
        console.log('🔍 Inscripciones disponibles:', details.participantEnrollments.map((pe: any) => ({
          participantId: pe.enrollment?.participant?.id,
          name: `${pe.enrollment?.participant?.firstName} ${pe.enrollment?.participant?.lastName}`
        })));
        console.log('🔍 Participante encontrado:', participant);
      }
      
      this.participantInfo = {
        name: participant ? 
          `${participant.firstName || participant.name || ''} ${participant.lastName || ''}`.trim() : 
          'Participante',
        document: participant ? 
          `${participant.typeDocument || participant.documentType || ''} ${participant.documentId || participant.document || ''}`.trim() : 
          'N/A',
        email: participant?.email || 'N/A'
      };
      
      console.log('👤 Información del participante configurada:', this.participantInfo);
    } else {
      console.warn('⚠️ No se recibieron detalles corporativos');
    }
  }


  subscribeToAllPaymentPlanChanges(): void {
    console.log('🔔 SUSCRIBIENDO A CAMBIOS - Total cuotas existentes:', this.plans.controls.length);
    this.plans.controls.forEach((control, index) => {
      control.valueChanges.subscribe(() => {
        console.log(`🔔 CAMBIO DETECTADO en cuota existente ${index + 1}`);
        this.validateAssignedAmount();
      });
    });
  }

  validateAssignedAmount(): void {
    console.log('🔄 INICIANDO VALIDACIÓN - Número de cuotas:', this.plans.controls.length);
    
    const total = this.refinanceSvc.totalAssignedPending(
      this.plans.controls.map(c => c.getRawValue())
    );
    const remaining = this.roundToTwo(this.remainingBalance);

    this.totalAssignedAmount = total;
    this.exceedsTotalAmount = total - remaining > 0.01;
    this.isExactMatch = this.refinanceSvc.isBalanced(total, remaining);

    console.log('🔍 ESTADO DE VALIDACIÓN FINAL:', {
      totalAssignedAmount: total,
      remainingBalance: remaining,
      isExactMatch: this.isExactMatch,
      exceedsTotalAmount: this.exceedsTotalAmount,
      formValid: this.form.valid,
      buttonEnabled: this.form.valid && this.isExactMatch && !this.exceedsTotalAmount
    });
  }

  private parseFormattedCurrency(formattedValue: string | number): number {
    return this.refinanceSvc.parseFormattedCurrency(formattedValue);
  }

  roundToTwo(value: number): number {
    return this.refinanceSvc.roundToTwo(value);
  }

  /**
   * 🆕 Suma del saldo pendiente (shortfall) de las cuotas PARTIALLY_PAID mantenidas
   * ('keep'). Ya aportan ese shortfall al total vía validateAssignedAmount, y
   * remainingBalance también lo incluye; por eso NO debe repartirse de nuevo entre
   * las cuotas nuevas al refinanciar (sino el total descuadra y bloquea Guardar).
   */
  private getPreservedPartialShortfall(): number {
    return this.refinanceSvc.preservedPartialShortfall(
      this.plans.controls.map(c => c.getRawValue())
    );
  }

  /** 🆕 Primer número libre considerando PAID + PARTIAL + editables actuales. */
  private get firstFreeInstallmentNumber(): number {
    return this.refinanceSvc.firstFreeInstallmentNumber(
      this.plans.controls.map(c => c.get('installmentNumber')?.value),
      this.paidInstallments.map(p => p.installmentNumber)
    );
  }

  addPlan(): void {
    const newPlan = this.createPlanGroup();
    newPlan.get('installmentNumber')?.setValue(this.firstFreeInstallmentNumber);
    newPlan.valueChanges.subscribe(() => this.validateAssignedAmount());
    this.plans.push(newPlan);
    this.validateAssignedAmount();
  }

  removePlan(i: number): void {
    const ctrl = this.plans.at(i);
    const num = ctrl.get('installmentNumber')?.value;
    const isAdded = ((ctrl as any).installmentId ?? null) === null;
    if (isAdded) {
      const addedIdx = this.addedInstallments.findIndex(a => a.installmentNumber === num);
      if (addedIdx >= 0) this.addedInstallments.splice(addedIdx, 1);
    }
    this.plans.removeAt(i);
    this.validateAssignedAmount();
  }

  /** @deprecated Numeración estable: ya no se renumera. */
  updateInstallmentNumbers(): void { /* no-op */ }

  // === MÉTODOS DE REFINANCIACIÓN ===

  openRefinanceModal(): void {
    this.refinanceInstallments = Math.max(2, this.plans.length || 2);
    this.showRefinanceModal = true;
  }

  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  confirmRefinance(): void {
    if (this.refinanceInstallments < this.minInstallments || this.refinanceInstallments > this.maxInstallments) {
      this.toast.showToast('error', `El número de cuotas debe estar entre ${this.minInstallments} y ${this.maxInstallments}.`);
      return;
    }

    this.applyRefinancing();
    this.closeRefinanceModal();
  }

  private applyRefinancing(): void {
    if (this.remainingBalance <= 0) {
      this.toast.showToast('warning', 'No hay saldo pendiente para refinanciar.');
      return;
    }

    // 🆕 Preservamos cuotas PAID (fuera del FormArray) y PARTIAL (en el FormArray)
    const preserved = this.plans.controls
      .filter(c => {
        const status = c.get('paymentStatus')?.value;
        return status === 'PAID' || status === 'PARTIALLY_PAID';
      })
      .map(c => ({ value: c.getRawValue(), installmentId: (c as any).installmentId }));

    while (this.plans.length !== 0) this.plans.removeAt(0);

    preserved.forEach(({ value, installmentId }) => {
      const g = this.createPlanGroup();
      g.patchValue(value);
      (g as any).installmentId = installmentId;
      g.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.plans.push(g);
    });

    // El shortfall de las parciales preservadas en 'keep' ya está incluido en
    // remainingBalance y lo recuenta el validador. Solo repartimos el resto.
    const amountToDistribute = this.roundToTwo(this.remainingBalance - this.getPreservedPartialShortfall());
    if (amountToDistribute <= 0) {
      this.toast.showToast('warning',
        'Las cuotas parciales mantenidas ya cubren el saldo restante. No hay monto para repartir en cuotas nuevas.');
      return;
    }

    const startingNumber = this.firstFreeInstallmentNumber;
    const amounts = this.refinanceSvc.splitEvenly(amountToDistribute, this.refinanceInstallments);

    for (let i = 0; i < this.refinanceInstallments; i++) {
      const amount = amounts[i];

      const formattedAmount = amount.toLocaleString('es-CO', {
        style: 'currency', currency: 'COP',
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
      const num = startingNumber + i;
      const dueDate = this.calculateNextDueDate(i + 1);

      const newPlanGroup = this.createPlanGroup();
      newPlanGroup.patchValue({
        dueDate,
        installmentNumber: num,
        amountToPay: formattedAmount,
        paymentStatus: 'PENDING',
        payerType: 'PARTICIPANT',
        isPunished: false,
        observations: `Cuota ${num} - Refinanciación automática participante`
      });
      newPlanGroup.valueChanges.subscribe(() => this.validateAssignedAmount());
      this.plans.push(newPlanGroup);
    }
    this.validateAssignedAmount();

    this.validateAssignedAmount();
    this.toast.showToast('success', `Plan del participante refinanciado exitosamente en ${this.refinanceInstallments} cuotas.`);
  }

  private calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + installmentNumber);
    
    if (nextMonth.getMonth() !== ((today.getMonth() + installmentNumber) % 12)) {
      nextMonth.setDate(0);
    }
    
    return nextMonth;
  }

  isValidRefinanceRange(): boolean {
    return this.refinanceInstallments >= this.minInstallments && 
           this.refinanceInstallments <= this.maxInstallments;
  }

  getEstimatedInstallmentAmount(): number {
    if (this.refinanceInstallments <= 0) return 0;
    const distributable = this.roundToTwo(this.remainingBalance - this.getPreservedPartialShortfall());
    return this.roundToTwo(Math.max(0, distributable) / this.refinanceInstallments);
  }

  save(): void {
    if (this.form.invalid || this.exceedsTotalAmount) {
      this.toast.showToast('error', 'Formulario inválido o monto excedido');
      return;
    }

    if (!this.isExactMatch) {
      this.toast.showToast('error', 'El total asignado debe coincidir exactamente con el saldo restante');
      return;
    }

    this.selectedReason = PaymentPlanVersionReason.Refinance;
    this.reasonComment = '';
    this.showReasonModal = true;
  }

  closeReasonModal(): void {
    this.showReasonModal = false;
  }

  confirmReasonAndSave(): void {
    if (!this.reasonComment || this.reasonComment.trim().length < 5) {
      this.toast.showToast('error', 'Debes escribir un comentario que justifique el cambio (mínimo 5 caracteres).');
      return;
    }

    const editablePayload = this.plans.value.map((plan: any) => ({
      dueDate: plan.dueDate,
      amountToPay: this.parseFormattedCurrency(plan.amountToPay),
      installmentNumber: plan.installmentNumber,
      observations: plan.observations || ''
    }));
    // 🆕 Cuotas PAID también van al payload (con su amount original) para que
    // el backend tenga el plan completo.
    const paidPayload = this.paidInstallments.map((p: any) => ({
      dueDate: p.dueDate,
      amountToPay: p.amountToPay,
      installmentNumber: p.installmentNumber,
      observations: p.observations || ''
    }));
    const allPayload = [...paidPayload, ...editablePayload]
      .sort((a, b) => a.installmentNumber - b.installmentNumber);

    this.showReasonModal = false;
    this.loading.show();
    this.corporateService.refinanceSingleParticipant(
      this.agreementId,
      this.participantId,
      allPayload,
      this.selectedReason,
      this.reasonComment.trim(),
      this.redistributionLogLines.length > 0 ? this.redistributionLogLines.join('\n') : undefined
    )
    .pipe(finalize(() => this.loading.hide()))
    .subscribe({
      next: (res: any) => {
        this.toast.showToast('success', 'Plan del participante actualizado. Nueva versión creada.');
        const excesses = res?.data?.generatedExcesses ?? [];
        if (excesses.length === 0) {
          this.redirectAfterSave();
          return;
        }
        // Hay excedentes generados: mostrar resumen y redirigir al cerrarlo.
        this.excessSummary = excesses;
      },
      error: (err) => {
        console.error('❌ ERROR AL GUARDAR:', err);
        const msg = err?.error?.message || err?.error || 'No se pudo guardar el plan del participante';
        this.toast.showToast('error', msg);
      }
    });
  }

  /** Cierra el modal de excedentes generados y continúa con la redirección. */
  onExcessSummaryClosed(): void {
    this.excessSummary = [];
    this.redirectAfterSave();
  }

  /** Vista previa del sobrepago de las cuotas ya pagadas (se convierte en excedente al guardar). */
  get overpaymentPreview(): { count: number; total: number } {
    return this.refinanceSvc.overpaymentPreview(
      this.paidInstallments.map(p => ({
        amountToPay: Number(p.amountToPay || 0),
        amountPaid: Number(p.amountPaid || 0)
      }))
    );
  }

  /**
   * 🆕 Redirige al view del acuerdo corporativo tras guardar.
   * Si el caller pasó `?returnTo=/dashboard/...` válido en queryParams, se respeta;
   * si no, fallback al view del acuerdo corporativo de origen.
   */
  private redirectAfterSave(): void {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (returnTo && returnTo.startsWith('/dashboard/')) {
      this.router.navigateByUrl(returnTo);
      return;
    }
    this.router.navigate(['/dashboard/enrollments/corporate/view-v2', this.agreementId]);
  }

  // === MATRÍCULA ANCLA ===

  /**
   * Faltante por reasignar = gap exacto que habilita Guardar:
   * remainingBalance (matrícula − pagado) − total asignado pendiente.
   * Coincide con `isExactMatch`, también con cuotas parciales.
   */
  getParticipantUnassigned(): number {
    return Math.max(0, this.roundToTwo(this.remainingBalance - this.totalAssignedAmount));
  }

  volverAInscripcion(): void {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (returnTo && returnTo.startsWith('/dashboard/')) this.router.navigateByUrl(returnTo);
    else this.router.navigate(['/dashboard/enrollments/corporate/view-v2', this.agreementId]);
  }

  // === MÉTODO DE DEBUG ===

  debugValidation(): void {
    console.log('🔧 DEBUG MANUAL ACTIVADO');
    console.log('🔧 Componente actual:', this);
    console.log('🔧 FormArray plans:', this.plans);
    console.log('🔧 Controles actuales:', this.plans.controls.length);
    
    this.plans.controls.forEach((control, index) => {
      const amountControl = control.get('amountToPay');
      console.log(`🔧 Cuota ${index + 1}:`, {
        value: amountControl?.value,
        type: typeof amountControl?.value,
        valid: amountControl?.valid,
        errors: amountControl?.errors
      });
    });
    
    console.log('🔧 remainingBalance:', this.remainingBalance);
    console.log('🔧 totalAssignedAmount ANTES:', this.totalAssignedAmount);
    
    this.validateAssignedAmount();
    
    console.log('🔧 totalAssignedAmount DESPUÉS:', this.totalAssignedAmount);
  }
}


